import { useState, useEffect, memo } from "react";
import AnimacionCarga from "./animacion_de_carga";
import TablaGenerica from "./tablas";
import { CSVLink } from "react-csv";
import BotonSOS from "./sos/boton_sos";
import Swal from "sweetalert2";
import { obtenerPQRS_Respuesta } from "../../functions/consultas";
import { crearRegistro, actualizarRegistro, eliminarRegistro, consultarSimple } from "../../functions/crud";
import { subirArchivoFirebase } from "../../functions/archivos_firebase";
import "bootstrap-icons/font/bootstrap-icons.css";

const columnas = [
  { label: "Fecha", key: "fecha" },
  { label: "Mensaje", key: "mensaje" },
  { label: "URL Documento", key: "url" },
  { label: "Foto", key: "url_foto1" }
];

const API_BASE = "https://coordiph.herokuapp.com/api/pqrs";

const SIN_DOCUMENTO = "No existe un documento anexado.";
const TAM_MAXIMO_MB = 10;

const normalizarClave = (texto = "") =>
  texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .replace(/[.#$[\]/]/g, "");

const obtenerCorreoDesdeNotificaciones = async (nit, direccion) => {
  const clave = normalizarClave(direccion);
  try {
    const correo = await consultarSimple(`notificaciones/${nit}/${clave}`);
    if (correo && typeof correo === "string") return correo;
  } catch (e) {
    console.warn("[PQRS] No se pudo leer notificaciones:", e);
  }
  const fallback = localStorage.getItem("correo_pqrs") || "";
  console.warn("[PQRS] Usando correo fallback desde localStorage:", fallback);
  return fallback;
};

const ListadoPqrsRespuesta = memo(() => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const capturarDatos = async () => {
    setCargando(true);
    try {
      const id = localStorage.getItem("id_pqrs");
      const data = await obtenerPQRS_Respuesta(id);
      const lista = Array.isArray(data)
        ? data.map((r, i) => ({ ...r, id: r?.id ?? String(i) }))
        : Object.entries(data || {}).map(([clave, r]) => ({ ...r, id: clave }));
      setDatos(lista);
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  };

  const esResidente = localStorage.getItem("Permisos") === "RESIDENTE";

  const editarRespuesta = async (fila) => {
    const { value: mensaje } = await Swal.fire({
      title: "Editar respuesta",
      input: "textarea",
      inputValue: fila.mensaje || "",
      inputPlaceholder: "Escriba aquí la respuesta...",
      inputAttributes: { rows: 6 },
      showCancelButton: true,
      confirmButtonText: "Guardar cambios",
      cancelButtonText: "Cancelar",
      width: 600,
      preConfirm: (valor) => {
        if (!valor || !valor.trim()) {
          Swal.showValidationMessage("La respuesta es requerida");
          return false;
        }
        return valor.trim();
      }
    });
    if (!mensaje || mensaje === fila.mensaje) return;

    try {
      const nit = localStorage.getItem("CodigoCliente");
      const id = localStorage.getItem("id_pqrs");
      await actualizarRegistro(`/coordi_ph/${nit}/pqrs/${id}/respuesta/${fila.id}/mensaje`, mensaje);
      await Swal.fire({ icon: "success", title: "Respuesta actualizada", timer: 1500, showConfirmButton: false });
      capturarDatos();
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar la respuesta." });
    }
  };

  const eliminarRespuesta = async (fila) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar esta respuesta?",
      text: "Esta acción no se puede deshacer.",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b"
    });
    if (!isConfirmed) return;

    try {
      const nit = localStorage.getItem("CodigoCliente");
      const id = localStorage.getItem("id_pqrs");
      await eliminarRegistro(`/coordi_ph/${nit}/pqrs/${id}/respuesta/${fila.id}`);
      await Swal.fire({ icon: "success", title: "Respuesta eliminada", timer: 1500, showConfirmButton: false });
      capturarDatos();
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar la respuesta." });
    }
  };

  const botonesAccion = [
    {
      texto: "Ver documento",
      icono: "bi-file-earmark-text",
      accion: (fila) => {
        if (!fila.url || fila.url === SIN_DOCUMENTO) {
          Swal.fire({
            icon: "info",
            title: "Sin documento",
            text: SIN_DOCUMENTO
          });
          return;
        }
        window.open(fila.url, "_blank", "noopener,noreferrer");
      }
    },
    ...(esResidente ? [] : [
      { texto: "Editar", icono: "bi-pencil", accion: editarRespuesta },
      { texto: "Eliminar", icono: "bi-trash", accion: eliminarRespuesta }
    ])
  ];

  const abrirModalRespuesta = async () => {
    const generarFormularioHTML = () => `
      <div style="display:flex;flex-direction:column;gap:16px;text-align:left;">
        <div>
          <label style="display:block;font-weight:600;margin-bottom:8px;color:#333;">
            Estado de la PQRS
          </label>
          <select id="estado_pqrs"
            style="width:100%;padding:12px;border:1px solid #ddd;border-radius:8px;font-size:14px;">
            <option value="Cerrado">Cerrado</option>
            <option value="En proceso de respuesta">En proceso de respuesta</option>
          </select>
        </div>
        <div>
          <label style="display:block;font-weight:600;margin-bottom:8px;color:#333;">
            Respuesta *
          </label>
          <textarea id="respuesta_pqrs"
            placeholder="Escriba aquí su respuesta detallada..."
            rows="6"
            style="width:95%;padding:12px;border:1px solid #ddd;border-radius:8px;font-size:14px;resize:vertical;"
          ></textarea>
        </div>
        <div>
          <label style="display:block;font-weight:600;margin-bottom:8px;color:#333;">
            Documento anexo (PDF, opcional)
          </label>
          <input type="file" id="documento_pqrs" accept="application/pdf,.pdf"
            style="width:95%;padding:10px;border:1px solid #ddd;border-radius:8px;font-size:14px;background:#fafafa;" />
          <small style="display:block;margin-top:6px;color:#777;">
            Solo archivos PDF, máximo ${TAM_MAXIMO_MB} MB.
          </small>
        </div>
        <div>
          <label style="display:block;font-weight:600;margin-bottom:8px;color:#333;">
            Foto (opcional)
          </label>
          <input type="file" id="foto_pqrs" accept="image/*"
            style="width:95%;padding:10px;border:1px solid #ddd;border-radius:8px;font-size:14px;background:#fafafa;" />
          <div id="preview_foto_pqrs" style="margin-top:8px;"></div>
        </div>
      </div>
    `;

    const { value: formValues } = await Swal.fire({
      title: "Crear Respuesta PQR",
      html: generarFormularioHTML(),
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Enviar Respuesta",
      cancelButtonText: "Cancelar",
      width: 600,
      didOpen: () => {
        const input = document.getElementById("foto_pqrs");
        input.addEventListener("change", () => {
          const foto = input.files?.[0];
          document.getElementById("preview_foto_pqrs").innerHTML = foto
            ? `<img src="${URL.createObjectURL(foto)}" style="width:80px;height:80px;object-fit:cover;border-radius:8px;border:2px solid #e0ddf8;" />`
            : "";
        });
      },
      preConfirm: () => {
        const estado = document.getElementById("estado_pqrs")?.value || "Cerrado";
        const respuesta = document.getElementById("respuesta_pqrs")?.value.trim() || "";
        const archivo = document.getElementById("documento_pqrs")?.files?.[0] || null;

        if (!respuesta) {
          Swal.showValidationMessage("La respuesta es requerida");
          return false;
        }
        if (archivo) {
          const esPdf =
            archivo.type === "application/pdf" ||
            archivo.name.toLowerCase().endsWith(".pdf");
          if (!esPdf) {
            Swal.showValidationMessage("El documento anexo debe ser un archivo PDF");
            return false;
          }
          if (archivo.size > TAM_MAXIMO_MB * 1024 * 1024) {
            Swal.showValidationMessage(`El documento no puede superar ${TAM_MAXIMO_MB} MB`);
            return false;
          }
        }
        const foto = document.getElementById("foto_pqrs")?.files?.[0] || null;
        if (foto && !foto.type.startsWith("image/")) {
          Swal.showValidationMessage("La foto debe ser una imagen");
          return false;
        }
        return { estado, respuesta, archivo, foto };
      }
    });

    if (formValues) {
      await guardarRespuesta(formValues.estado, formValues.respuesta, formValues.archivo, formValues.foto);
    }
  };

  const guardarRespuesta = async (estado, respuesta, archivo = null, foto = null) => {
    Swal.fire({
      title: "Guardando...",
      text: "Por favor espere",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const id = localStorage.getItem("id_pqrs");
      const nit = localStorage.getItem("CodigoCliente");
      const asunto = localStorage.getItem("asunto_pqrs") || "Sin asunto";
      const mensajeOriginal = localStorage.getItem("mensaje_original") || "Sin mensaje original";
      const direccion = localStorage.getItem("casa_pqrs") || "";
      const cliente = localStorage.getItem("RazonSocial") || "Sin unidad";
      const ahora = new Date();
      const dos = (n) => String(n).padStart(2, "0");
      // Mismo formato que la app: yyyy-MM-dd HH:mm:ss
      const fechaActual = `${ahora.getFullYear()}-${dos(ahora.getMonth() + 1)}-${dos(ahora.getDate())} ${dos(ahora.getHours())}:${dos(ahora.getMinutes())}:${dos(ahora.getSeconds())}`;

      const correo = await obtenerCorreoDesdeNotificaciones(nit, direccion);

      let url = SIN_DOCUMENTO;

      if (archivo) {
        Swal.update({ text: "Subiendo documento anexo..." });
        const marca = new Date().toISOString().replace(/[:.]/g, "-");
        const ruta = `pqrs/${nit}/Documento Id${marca}.pdf`;
        // El PDF es opcional: si falla la subida, la respuesta se guarda sin él.
        url = await subirArchivoFirebase(ruta, archivo).catch((e) => {
          console.warn("[PQRS] No se pudo subir el documento:", e);
          return SIN_DOCUMENTO;
        });
        Swal.update({ text: "Por favor espere" });
      }

      let url_foto1 = "";
      if (foto) {
        Swal.update({ text: "Subiendo foto..." });
        const marca = new Date().toISOString().replace(/[:.]/g, "-");
        const ext = (foto.name.split(".").pop() || "jpeg").toLowerCase();
        // La foto es opcional: si falla la subida, la respuesta se guarda sin ella.
        url_foto1 = await subirArchivoFirebase(`pqrs/${nit}/Respuesta Id${marca}foto_0.${ext}`, foto).catch((e) => {
          console.warn("[PQRS] No se pudo subir la foto:", e);
          return "";
        });
        Swal.update({ text: "Por favor espere" });
      }

      const nuevaRespuesta = {
        mensaje: respuesta,
        fecha: fechaActual,
        url,
        url_foto1
      };

      await actualizarRegistro(`/coordi_ph/${nit}/pqrs/${id}/estado`, estado);
      await crearRegistro(`/coordi_ph/${nit}/pqrs/${id}/respuesta`, nuevaRespuesta);

      if (correo) {
        await fetch(`${API_BASE}/enviarcorreo`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            correoElectronico: correo,
            asunto,
            mensajeOriginal,
            mensaje: respuesta,
            direccion,
            cliente,
            // Igual que la app: la fecha del correo es la de la PQRS original.
            fechaEvento: localStorage.getItem("fecha_pqrs") || fechaActual,
            codigoCliente: nit,
            urlDocumento: url,
            urlFoto: url_foto1
          })
        });
      } else {
        console.warn("[PQRS] Sin correo disponible — no se envió notificación.");
      }

      await Swal.fire({
        icon: "success",
        title: "Respuesta guardada",
        text: correo
          ? "La respuesta fue almacenada y el correo enviado exitosamente."
          : "La respuesta fue almacenada. No se encontró correo para notificar.",
        timer: 2000,
        showConfirmButton: false
      });

      capturarDatos();

    } catch (error) {
      console.error("Error al guardar:", error);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo guardar la respuesta." });
    }
  };

  useEffect(() => { capturarDatos(); }, []);

  return (
    <>
      {cargando ? (
        <AnimacionCarga />
      ) : (
        <>
          <TablaGenerica
            datos={datos}
            titulos={columnas.map(c => c.label.toUpperCase())}
            campos={columnas.map(c => c.key)}
            elementosPorPagina={3000}
            columnaOrdenInicial={0}
            direccionOrdenInicial="desc"
            botones={botonesAccion}
            contenidoExtra={
              <div style={{ marginLeft: "auto", display: "flex", gap: 0 }}>
                <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
                <CSVLink data={datos} headers={columnas} filename="listado_pqrs_respuestas.csv" separator=";" style={{ padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" }} title="Exportar"><i className="bi bi-download" /></CSVLink>
                {localStorage.getItem("Permisos") !== "RESIDENTE" && (
                  <button onClick={abrirModalRespuesta} title="Nueva Respuesta"><i className="bi bi-plus-lg" /></button>
                )}
              </div>
            }
          />

          <BotonSOS />
        </>
      )}
    </>
  );
});

export default ListadoPqrsRespuesta;